import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { toast } from "sonner";
import { waitingHeadline, errorWords } from "@alm/domain";
import { PageHeader } from "@/components/app/shell/page-header";
import { WorkspaceMark } from "@/components/app/workspace/workspace-mark";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { useWaitingSummary } from "@/lib/query/use-waiting";
import { needsReply } from "@/routes/_authenticated/_front-desk/sarah/-components/sms-threads";
import { useHandledCheck } from "@/routes/_authenticated/_front-desk/sarah/-components/use-handled-check";
import { CallLine } from "./-components/call-line";
import { DeskPhone } from "./-components/desk-phone";
import { OnDuty } from "./-components/on-duty";
import { OwedTextsPanel } from "./-components/owed-texts-panel";
import { useDeskInbox } from "./-components/use-desk-inbox";
import { WaitingCallsPanel } from "./-components/waiting-calls-panel";
import { Panel } from "@/routes/_authenticated/ws/sarah/-components/sarah-panel";
import { Rows } from "@/routes/_authenticated/ws/sarah/-components/sarah-rows";
import { QuietEmpty } from "@/routes/_authenticated/ws/sarah/-components/quiet-empty";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { useZoneFor } from "@/lib/facility/zone";

export const Route = createFileRoute("/_authenticated/ws/sarah/")({
  beforeLoad: ({ context }) => guardRoute(context.me, "sarah-calls"),
  component: SarahToday,
});

/** Three questions in the order someone sitting down asks: is my telephone working, who is waiting
 * on us, who could I reach. The title is the server's one count, the same the rail and the phone
 * show; anything older than a week folds into one row in each list. */
function SarahToday() {
  const { facilityId } = useFacility();
  const zoneFor = useZoneFor();
  const canClose = useCan("frontdesk:call");
  const queryClient = useQueryClient();
  const today = format(new Date(), "yyyy-MM-dd");
  const scope = facilityId === "all" ? undefined : facilityId;

  const waiting = useQuery({
    ...orpc.frontDesk.calls.list.queryOptions({
      input: { facilityId: scope, tab: "attention", limit: 50, offset: 0 },
    }),
    refetchInterval: 15_000,
  });
  const todays = useQuery({
    ...orpc.frontDesk.calls.list.queryOptions({
      input: { facilityId: scope, from: today, to: today, limit: 8, offset: 0 },
    }),
    refetchInterval: 15_000,
  });
  const me = useQuery(orpc.you.deskLine.queryOptions());
  const roster = useQuery({
    ...orpc.frontDesk.staff.list.queryOptions({ input: { facilityId: scope } }),
    staleTime: 60_000,
  });
  const inbox = useDeskInbox(facilityId);
  const summary = useWaitingSummary(facilityId);
  /* The server's owed set leaves colleagues and handled threads out; until it arrives, the last
     text decides. */
  const owed = summary.data
    ? new Set(summary.data.threads.map((thread) => `${thread.facilityId}|${thread.threadKey}`))
    : null;
  const textsWaiting = inbox.threads.filter((thread) =>
    owed ? owed.has(`${thread.facilityId}|${thread.key}`) : needsReply(thread),
  );

  const triage = useMutation(
    orpc.frontDesk.calls.triage.mutationOptions({
      onSuccess: async () => {
        toast.success("Marked handled");
        await queryClient.invalidateQueries({ queryKey: orpc.frontDesk.key() });
      },
      onError: (error: Error, input) => {
        if (!check.recover(error, input.callId)) toast.error(errorWords(error));
      },
    }),
  );
  const check = useHandledCheck(triage.mutate);

  const counted = todays.data?.counts;
  /* A failed read is not "nobody waiting": the title says so only once the count is in. */
  const known = summary.data;
  return (
    <div className="mx-auto w-full max-w-6xl space-y-5">
      {check.dialog}
      <PageHeader
        mark={<WorkspaceMark id="sarah" size={44} />}
        kicker={`Sarah, Front desk, ${format(new Date(), "EEE d MMM")}`}
        title={known ? waitingHeadline(known.calls, known.texts) : "The desk"}
        meta={
          (counted
            ? counted.all === 0
              ? "No new calls today"
              : `${String(counted.all)} ${counted.all === 1 ? "call" : "calls"} today, ${String(counted.handled)} handled`
            : null) ?? "Sarah answers every call and text. This is what she handed to a person."
        }
      />

      <DeskPhone />

      <div className="grid-cols-[minmax(0,1fr)] grid items-start gap-5 lg:grid-cols-3">
        <WaitingCallsPanel
          facilityId={facilityId}
          rows={waiting.data?.rows ?? []}
          olderCount={summary.data?.older.calls ?? 0}
          olderBefore={summary.data?.olderBefore}
          me={me.data}
          roster={roster.data}
          pending={waiting.isPending}
          error={waiting.error}
          retry={() => void waiting.refetch()}
          retrying={waiting.isRefetching}
          onHandled={canClose ? check.tick : undefined}
        />

        <Panel title="Who can take a call">
          <OnDuty facilityId={facilityId} />
        </Panel>
      </div>

      <div className="grid-cols-[minmax(0,1fr)] grid items-start gap-5 lg:grid-cols-3">
        <Panel className="lg:col-span-2" title="Today's calls" to="/ws/sarah/calls">
          {todays.isPending ? (
            <Rows />
          ) : todays.isError ? (
            <ReadFailed
              message="Couldn't load today's calls."
              error={todays.error}
              onRetry={() => void todays.refetch()}
              retrying={todays.isRefetching}
              className="p-4"
            />
          ) : (todays.data?.rows ?? []).length === 0 ? (
            <QuietEmpty>No calls yet today.</QuietEmpty>
          ) : (
            <ul className="space-y-0.5 p-2">
              {(todays.data?.rows ?? []).map((row) => (
                <CallLine
                  key={row.id}
                  row={row}
                  timeZone={zoneFor(row.facilityId)}
                  me={me.data}
                  roster={roster.data}
                />
              ))}
            </ul>
          )}
        </Panel>

        <OwedTextsPanel
          facilityId={facilityId}
          threads={textsWaiting}
          olderCount={summary.data?.older.texts ?? 0}
          olderBefore={summary.data?.olderBefore}
          pending={inbox.messages.isPending}
          error={inbox.messages.error}
          retry={() => void inbox.messages.refetch()}
          retrying={inbox.messages.isRefetching}
        />
      </div>
    </div>
  );
}
